import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';

import { getMyAnswers } from '../../services/surveyService';
import Breadcrumbs from '../../components/ui/Breadcrumbs';

const Container = styled.div``;

const Banner = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  background: #f0fdf4;
  border-radius: 6px;
  padding: 14px 20px;
  margin-bottom: 24px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #16a34a;
`;

const QuestionCard = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 24px;
  margin-bottom: 16px;
  box-shadow: 0 1px 4px rgba(0, 66, 66, 0.06);
`;

const QuestionText = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 15px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 4px;
`;

const TypeBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 11px;
  font-weight: 600;
  background: ${({ $color }) => $color + '15'};
  color: ${({ $color }) => $color};
  margin-bottom: 16px;
`;

const OptionItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 4px;
  margin-bottom: 6px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${({ $selected }) => $selected ? '#eff6ff' : '#f8fafc'};
  border: 1.5px solid ${({ $selected }) => $selected ? '#2563eb' : 'transparent'};
  opacity: 0.85;
`;

const AnswerText = styled.div`
  background: #f8fafc;
  border-radius: 4px;
  padding: 12px 16px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const NoData = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  color: #A0A8B4;
  text-align: center;
  padding: 60px 0;
`;

const typeColors = {
  singleChoice: '#2563eb',
  singleChoiceAlternate: '#3b82f6',
  multipleChoice: '#16a34a',
  multipleChoiceAlternate: '#ca8a04',
  ranking: '#dc2626',
  freeText: '#9333ea',
};

export default function SurveyAnswersPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getMyAnswers(id)
      .then((res) => setItems(res.data?.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <Container><NoData>...</NoData></Container>;
  if (!items.length) return <Container><NoData>{t('survey.noRecords')}</NoData></Container>;

  return (
    <Container>
      <Breadcrumbs items={[
        { label: t('survey.mySurveys'), path: '/my-surveys' },
        { label: t('survey.viewAnswers') },
      ]} />

      <Banner>
        <CheckCircle size={18} />
        {t('survey.submitted')}
      </Banner>

      {items.map((item, idx) => (
        <QuestionCard key={item.questionId}>
          <QuestionText>{idx + 1}. {item.questionText}</QuestionText>
          <TypeBadge $color={typeColors[item.questionType]}>{t(`survey.${item.questionType}`)}</TypeBadge>

          {(item.questionType === 'singleChoice' || item.questionType === 'singleChoiceAlternate') && (
            <div>
              {(item.options || []).map((opt) => (
                <OptionItem key={opt.id} $selected={item.selectedOptionId === opt.id}>
                  {opt.label}
                </OptionItem>
              ))}
              {item.questionType === 'singleChoiceAlternate' && item.alternateText && (
                <AnswerText style={{ marginTop: 8 }}>
                  {t('survey.other')}: {item.alternateText}
                </AnswerText>
              )}
            </div>
          )}

          {(item.questionType === 'multipleChoice' || item.questionType === 'multipleChoiceAlternate') && (
            <div>
              {(item.options || []).map((opt) => (
                <OptionItem key={opt.id} $selected={(item.selectedOptionIds || []).includes(opt.id)}>
                  {opt.label}
                </OptionItem>
              ))}
              {item.questionType === 'multipleChoiceAlternate' && item.alternateText && (
                <AnswerText style={{ marginTop: 8 }}>
                  {t('survey.other')}: {item.alternateText}
                </AnswerText>
              )}
            </div>
          )}

          {item.questionType === 'ranking' && (
            <div>
              {(item.selectedOptionIds || []).map((optId, ri) => {
                const opt = (item.options || []).find(o => o.id === optId);
                return (
                  <OptionItem key={optId} $selected>
                    <span style={{ fontWeight: 700, color: '#dc2626', marginRight: 6 }}>{ri + 1}.</span>
                    {opt ? opt.label : optId}
                  </OptionItem>
                );
              })}
            </div>
          )}

          {item.questionType === 'freeText' && (
            <AnswerText>{item.textValue || '-'}</AnswerText>
          )}
        </QuestionCard>
      ))}
    </Container>
  );
}
